import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
// Import necessary icons and components
import { CheckCircle, Clock, AlertTriangle, ChevronRight, Download, MapPin, Calendar } from 'lucide-react'; // Added MapPin, Calendar
import { Select, Button, Dropdown, Menu, Space } from 'antd'; // Ensure all needed Antd components are imported
import { getMyAssignedObservations, getAreas } from '../../services/observationAPI'; // Using getMyAssignedObservations and getAreas

import toast from 'react-hot-toast';

// Import report generation libraries
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { Document, Packer, Paragraph, Table, TableCell, TableRow, TextRun, WidthType } from 'docx';
import { saveAs } from 'file-saver';

const MyActions = () => {
    // State declarations
    const [observations, setObservations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all'); // Status filter
    const [areas, setAreas] = useState([]); // State for areas data
    const [selectedArea, setSelectedArea] = useState(''); // Area filter (stores area ID)
    const [areaMap, setAreaMap] = useState({}); // Map area ID to name

    const navigate = useNavigate();

    // Fetch observations and areas
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            // Fetch assigned observations and area data in parallel
            const [observationsData, areasData] = await Promise.all([
                getMyAssignedObservations(), // Use the specific API call for assigned observations
                getAreas() // Fetch areas for filtering and reporting
            ]);

            // Ensure data received is an array, provide fallback
            const fetchedObservations = observationsData?.observations || [];
            const fetchedAreas = areasData?.data || [];

            setObservations(fetchedObservations);
            setAreas(fetchedAreas); // Set the areas state

            // Populate Area Map: Create a map of { areaId: areaName }
            const map = fetchedAreas.reduce((acc, area) => {
                if (area._id && area.name) {
                    acc[area._id] = area.name;
                }
                return acc;
            }, {});
            setAreaMap(map); // Set the area map state

        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load data');
            // Reset state on error
            setObservations([]);
            setAreas([]);
            setAreaMap({});
        } finally {
            setLoading(false);
        }
    }, []); // Empty dependency array: fetchData is created once

    useEffect(() => {
        fetchData();
    }, [fetchData]); // Run effect when fetchData changes (only on mount)

    // Helper Function to Get Area Name by ID
    const getAreaNameById = useCallback((areaId) => {
        // Handle cases where observation.area might be the object or just the ID string
        const id = typeof areaId === 'object' && areaId !== null ? areaId._id : areaId;
        return areaMap[id] || 'Unknown Area'; // Return name or fallback
    }, [areaMap]); // Re-create only if areaMap changes

    // Filter observations based on status and area
    const filteredObservations = observations.filter(observation => {
        const statusMatch = filter === 'all' || observation.status === filter;
        // Handle area being an object or string ID for comparison
        const observationAreaId = typeof observation.area === 'object' && observation.area !== null ? observation.area._id : observation.area;
        const areaMatch = !selectedArea || (observationAreaId && observationAreaId === selectedArea);
        // Return true only if both conditions match
        return statusMatch && areaMatch;
    });

    // --- Report Generation Functions ---
    // Prepare data for reports based on current filters
    const prepareReportData = useCallback(() => {
        return filteredObservations.map(obs => ({
            description: obs.description || 'N/A',
            area: getAreaNameById(obs.area), // Use the helper function
            // Define 'Action' - using description as placeholder
            action: obs.actionTaken || obs.description || 'N/A',
            // Format status
            status: obs.status ? obs.status.charAt(0).toUpperCase() + obs.status.slice(1) : 'N/A',
        }));
    }, [filteredObservations, getAreaNameById]); // Dependencies

    // Generate PDF Report
    const generatePdf = useCallback(() => {
        if (filteredObservations.length === 0) {
            toast.error("No data to export for the current filter.");
            return;
        }
        const doc = new jsPDF();
        const tableData = prepareReportData();
        const tableColumn = ["Observation Description", "Area", "Action", "Response Status"];
        const tableRows = tableData.map(item => [
            item.description, item.area, item.action, item.status
        ]);
        doc.text("My Assigned Observations Report", 14, 15); // Updated Title
        autoTable(doc, {
            head: [tableColumn], body: tableRows, startY: 20,
            headStyles: { fillColor: [22, 160, 133] }, // Example style
            alternateRowStyles: { fillColor: [240, 240, 240] }, // Example style
        });
        doc.save("my_assigned_observations_report.pdf"); // Updated filename
        toast.success("PDF report downloaded!");
    }, [prepareReportData, filteredObservations.length]);

    // Generate Excel Report
    const generateExcel = useCallback(() => {
        if (filteredObservations.length === 0) {
            toast.error("No data to export for the current filter.");
            return;
        }
        const tableData = prepareReportData();
        const worksheetData = tableData.map(item => ({
            "Observation Description": item.description, "Area": item.area,
            "Action": item.action, "Response Status": item.status
        }));
        const worksheet = XLSX.utils.json_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "My Assigned Observations"); // Updated sheet name
        worksheet["!cols"] = [ { wch: 40 }, { wch: 20 }, { wch: 40 }, { wch: 15 }]; // Example column widths
        XLSX.writeFile(workbook, "my_assigned_observations_report.xlsx"); // Updated filename
        toast.success("Excel report downloaded!");
    }, [prepareReportData, filteredObservations.length]);

    // Generate Word Report
    const generateWord = useCallback(async () => {
        if (filteredObservations.length === 0) {
            toast.error("No data to export for the current filter.");
            return;
        }
        try {
            const tableData = prepareReportData();
            const columnHeaders = ["Observation Description", "Area", "Action", "Response Status"];
            const headerRow = new TableRow({
                children: columnHeaders.map(header => new TableCell({
                     children: [new Paragraph({ children: [new TextRun({ text: header, bold: true })] })],
                })), tableHeader: true,
            });
            const dataRows = tableData.map(item => new TableRow({
                children: [
                    new TableCell({ children: [new Paragraph(item.description)] }),
                    new TableCell({ children: [new Paragraph(item.area)] }),
                    new TableCell({ children: [new Paragraph(item.action)] }),
                    new TableCell({ children: [new Paragraph(item.status)] }),
                ],
            }));
            const table = new Table({ rows: [headerRow, ...dataRows], width: { size: 100, type: WidthType.PERCENTAGE } });
            const doc = new Document({
                sections: [{
                    properties: {},
                    children: [
                        new Paragraph({ children: [new TextRun({ text: "My Assigned Observations Report", bold: true, size: 28 })], spacing: { after: 200 } }), // Updated Title
                        table,
                    ],
                }],
            });
            const blob = await Packer.toBlob(doc);
            saveAs(blob, "my_assigned_observations_report.docx"); // Updated filename
            toast.success("Word report downloaded!");
        } catch (error) {
            console.error("Error generating Word document:", error);
            toast.error("Failed to generate Word report.");
        }
    }, [prepareReportData, filteredObservations.length]);
    // --- End Report Generation Functions ---

    // Get status color
    const getStatusColor = (status) => {
        switch (status) {
            case 'waiting': return 'text-amber-500';
            case 'in-progress': return 'text-purple-500';
            case 'submitted': return 'text-blue-500';
            case 'completed': return 'text-green-500';
            default: return 'text-gray-500';
        }
    };

    // Get status icon
    const getStatusIcon = (status) => {
        switch (status) {
            case 'waiting': return <Clock className="h-5 w-5" />;
            case 'in-progress': return <AlertTriangle className="h-5 w-5" />;
            case 'submitted':
            case 'completed': return <CheckCircle className="h-5 w-5" />;
            default: return null;
        }
    };

    // Handle action click (navigates based on status)
    const handleActionClick = (observationId, status) => {
        switch (status) {
            case 'waiting': navigate(`/my-action/waiting/${observationId}`); break;
            case 'in-progress': navigate(`/my-action/in-progress/${observationId}`); break;
            case 'submitted': navigate(`/my-action/submitted/${observationId}`); break;
            case 'completed': navigate(`/my-action/completed/${observationId}`); break;
            default: navigate(`/employee/review-action/${observationId}`); // Fallback route
        }
    };

    // Define Download Menu Items
    const downloadMenu = (
        <Menu onClick={({ key }) => { // Handle menu item clicks
            if (key === 'pdf') generatePdf();
            else if (key === 'excel') generateExcel();
            else if (key === 'word') generateWord();
        }}>
            <Menu.Item key="pdf">Download as PDF</Menu.Item>
            <Menu.Item key="excel">Download as Excel</Menu.Item>
            <Menu.Item key="word">Download as Word</Menu.Item>
        </Menu>
    );

    // Loading State UI
    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    // Main Component Render
    return (
        // Apply responsive padding and background
        <div className="min-h-screen p-4">
            {/* Max width container for content */}
            <div className="max-w-7xl mx-auto space-y-6">
                {/* Header Section: Title and Download Button */}
                 {/* Responsive flex layout */}
                 <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                    {/* Title and Subtitle */}
                    <div>
                        {/* Responsive text size */}
                        <h1 className="text-xl sm:text-2xl font-bold text-gray-800">My Actions</h1>
                        <p className="text-sm sm:text-base text-gray-600">View, manage, and report your assigned actions.</p>
                    </div>

                    {/* Download Button Wrapper */}
                    <div className="w-full sm:w-auto">
                        <Dropdown
                            overlay={downloadMenu}
                            placement="bottomRight"
                            disabled={filteredObservations.length === 0}
                            className="w-full sm:w-auto" // Full width on mobile
                        >
                            <Button type="primary" icon={<Download size={18} />} className="w-full sm:w-auto flex justify-center items-center">
                                Download Report
                            </Button>
                        </Dropdown>
                    </div>
                 </div>


                {/* Filter Section: Area and Status */}
                {/* Responsive flex layout and styling */}
                <div className="flex flex-col lg:flex-row flex-wrap gap-4 items-stretch lg:items-center bg-white p-4 rounded-lg shadow">
                    {/* Area Filter */}
                     <div className="flex items-center gap-2 w-full lg:w-auto">
                         {/* <label htmlFor="area-filter" className="font-medium text-gray-700 text-sm flex-shrink-0">Area:</label> */}
                         {/* Responsive Select component */}
                         <Select
                             id="area-filter"
                             value={selectedArea}
                             onChange={(value) => setSelectedArea(value)}
                             className="w-full" // Full width within container
                             style={{ minWidth: 180 }} // Minimum width
                             showSearch
                             placeholder="Filter by Area"
                             allowClear
                             filterOption={(input, option) =>
                                 (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                             }
                         >
                             <Select.Option value="">All Areas</Select.Option>
                             {/* Populate areas from state */}
                             {areas.map(area => (
                                 <Select.Option key={area._id} value={area._id}>
                                     {area.name}
                                 </Select.Option>
                             ))}
                         </Select>
                     </div>

                    {/* Status Filter Buttons */}
                     <div className="flex items-center gap-2 flex-wrap w-full lg:w-auto">
                         <span className="font-medium text-gray-700 text-sm flex-shrink-0">Status:</span>
                         {/* Responsive button group */}
                         <div className="flex flex-wrap gap-2">
                             {/* Generate status buttons */}
                             {['all', 'waiting', 'in-progress', 'submitted', 'completed'].map(statusKey => {
                                const isActive = filter === statusKey;
                                // Define base, active, and inactive styles
                                let baseClasses = "px-3 py-1 text-xs sm:text-sm rounded font-medium transition-colors";
                                let activeClasses = "";
                                let inactiveClasses = "bg-gray-200 text-gray-700 hover:bg-gray-300";

                                // Determine active styles based on status
                                if (isActive) {
                                    switch(statusKey) {
                                        case 'all': activeClasses = 'bg-blue-600 text-white'; break;
                                        case 'waiting': activeClasses = 'bg-amber-500 text-white'; break;
                                        case 'in-progress': activeClasses = 'bg-purple-500 text-white'; break;
                                        case 'submitted': activeClasses = 'bg-blue-500 text-white'; break;
                                        case 'completed': activeClasses = 'bg-green-500 text-white'; break;
                                        default: activeClasses = inactiveClasses;
                                    }
                                }

                                return (
                                    <button
                                        key={statusKey}
                                        onClick={() => setFilter(statusKey)}
                                        className={`${baseClasses} ${isActive ? activeClasses : inactiveClasses}`}
                                    >
                                        {/* Format button text */}
                                        {statusKey.charAt(0).toUpperCase() + statusKey.slice(1).replace('-', ' ')}
                                    </button>
                                );
                             })}
                         </div>
                     </div>
                </div>

                {/* Actions List */}
                <div className="bg-white shadow overflow-hidden rounded-lg">
                    {/* List container */}
                    <ul className="divide-y divide-gray-200">
                        {filteredObservations.length === 0 ? (
                            // Message when list is empty
                            <li className="px-4 sm:px-6 py-10 text-center text-gray-500">
                                No actions found matching the selected filters.
                            </li>
                        ) : (
                            // Map through filtered observations to create list items
                            filteredObservations.map(observation => (
                                <li
                                    key={observation._id}
                                    className="px-4 sm:px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors duration-150"
                                    onClick={() => handleActionClick(observation._id, observation.status)}
                                    role="button" tabIndex={0}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleActionClick(observation._id, observation.status)}}
                                >
                                    {/* Responsive layout for list item content */}
                                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                                        {/* Left side content */}
                                        <div className="flex items-start gap-3 flex-1 min-w-0">
                                            {/* Status Icon */}
                                            <span className={`flex-shrink-0 mt-1 ${getStatusColor(observation.status)}`}>
                                                {getStatusIcon(observation.status)}
                                            </span>
                                            {/* Observation Details */}
                                            <div className="flex-1 min-w-0">
                                                {/* Description */}
                                                <p className="text-sm font-medium text-gray-900 break-words">
                                                    {observation.description || 'No Description'}
                                                </p>
                                                {/* Meta Info (Status, Area, Date) */}
                                                <div className="mt-1 flex flex-wrap items-center text-xs text-gray-500 gap-x-3 gap-y-1">
                                                    {/* Status Badge */}
                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full font-medium ${getStatusColor(observation.status)} bg-opacity-10 ${
                                                        observation.status === 'waiting' ? 'bg-amber-100' :
                                                        observation.status === 'in-progress' ? 'bg-purple-100' :
                                                        observation.status === 'submitted' ? 'bg-blue-100' :
                                                        observation.status === 'completed' ? 'bg-green-100' : 'bg-gray-100'
                                                        }`}>
                                                        {observation.status ? observation.status.charAt(0).toUpperCase() + observation.status.slice(1) : 'Unknown'}
                                                    </span>
                                                    {/* Area Name */}
                                                    <span className="inline-flex items-center">
                                                        <MapPin className="h-3 w-3 mr-1 text-gray-400" />
                                                        {getAreaNameById(observation.area)}
                                                    </span>
                                                    {/* Creation Date */}
                                                    <span className="inline-flex items-center">
                                                        <Calendar className="h-3 w-3 mr-1 text-gray-400" />
                                                        Created: {new Date(observation.createdAt).toLocaleDateString()}
                                                    </span>
                                                </div>
                                                {/* Rejection Reason (if exists) */}
                                                {observation.rejectionReason && (
                                                    <p className="mt-1 text-xs text-red-600">
                                                        Rejection reason: {observation.rejectionReason}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        {/* Right side: Chevron Icon */}
                                        <div className="flex-shrink-0 self-center sm:ml-4">
                                            <ChevronRight className="h-5 w-5 text-gray-400" />
                                        </div>
                                    </div>
                                </li>
                            ))
                        )}
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default MyActions;
